"use client";

import { useRouter, usePathname } from "next/navigation";
import { useMemo, useCallback, useState, useTransition } from "react";

import { useUser } from "@/hooks/useUser";
import { useApi } from "@/lib/fetch-client";

import type { Organization, Project } from "@/lib/types";

interface UseDashboardStateProps {
	initialOrganizationsData?: unknown;
	initialProjectsData?: unknown;
	selectedOrgId?: string;
	selectedProjectId?: string;
}

export function useDashboardState({
	initialOrganizationsData,
	initialProjectsData,
	selectedOrgId,
	selectedProjectId,
}: UseDashboardStateProps = {}) {
	const router = useRouter();
	const pathname = usePathname();
	const api = useApi();
	const [isPending, startTransition] = useTransition();
	const [isNavigating, setIsNavigating] = useState(false);

	useUser({ redirectTo: "/login", redirectWhen: "unauthenticated" });

	// Extract orgId from URL pathname if not provided
	// URL pattern: /dashboard/{orgId}/... or /dashboard/{orgId}/{projectId}/...
	const orgIdFromUrl = useMemo(() => {
		const pathParts = pathname.split("/");
		// pathParts[0] = '', pathParts[1] = 'dashboard', pathParts[2] = orgId
		return pathParts[2] || null;
	}, [pathname]);

	// Use provided selectedOrgId, fallback to URL, then to first org
	const effectiveOrgId = selectedOrgId || orgIdFromUrl;

	// Fetch organizations
	const { data: organizationsData, isLoading: isLoadingOrgs } = api.useQuery(
		"get",
		"/orgs",
		{},
		{
			initialData: initialOrganizationsData as
				| { organizations: Organization[] }
				| undefined,
			staleTime: 5 * 60 * 1000, // 5 minutes
			refetchOnWindowFocus: false,
		},
	);
	const organizations = useMemo(
		() => organizationsData?.organizations || [],
		[organizationsData?.organizations],
	);

	// Derive selected organization from props or default to first
	const selectedOrganization = useMemo(() => {
		if (effectiveOrgId) {
			return organizations.find((org) => org.id === effectiveOrgId) || null;
		}
		return organizations[0] || null;
	}, [effectiveOrgId, organizations]);

	// Fetch projects for selected organization
	const { data: projectsData, isLoading: isLoadingProjects } = api.useQuery(
		"get",
		"/orgs/{id}/projects",
		{
			params: {
				path: {
					id: selectedOrganization?.id || "",
				},
			},
		},
		{
			enabled: !!selectedOrganization?.id,
			initialData: initialProjectsData as { projects: Project[] } | undefined,
			staleTime: 5 * 60 * 1000, // 5 minutes
			refetchOnWindowFocus: false,
		},
	);

	// Get current projects from query data
	const projects = useMemo(
		() => projectsData?.projects || [],
		[projectsData?.projects],
	);

	// Derive selected project from props
	const selectedProject = useMemo(() => {
		if (selectedProjectId && projects.length > 0) {
			return (
				projects.find((project) => project.id === selectedProjectId) || null
			);
		}
		return projects[0] || null;
	}, [selectedProjectId, projects]);

	// Navigation functions for the new route structure
	const handleOrganizationCreated = useCallback(
		(org: Organization) => {
			// Navigate to the new organization with first project
			router.push(`/dashboard/${org.id}`);
		},
		[router],
	);

	const handleProjectCreated = useCallback(
		(project: Project) => {
			// Navigate to the new project
			router.push(`/dashboard/${project.organizationId}/${project.id}`);
		},
		[router],
	);

	const handleOrganizationSelect = useCallback(
		(org: Organization | null) => {
			if (org?.id) {
				// Set loading state
				setIsNavigating(true);

				// Use transition for smoother UI updates
				startTransition(() => {
					// Navigate to the new organization (will redirect to first project)
					router.push(`/dashboard/${org.id}`);
					// Reset loading state after a short delay
					setTimeout(() => setIsNavigating(false), 500);
				});
			}
		},
		[router, startTransition],
	);

	const handleProjectSelect = useCallback(
		(project: Project | null) => {
			if (project?.id) {
				// Set loading state
				setIsNavigating(true);

				// Extract the current page from pathname (e.g., 'api-keys', 'provider-keys', etc.)
				const pathParts = pathname.split("/");
				const currentPage = pathParts[4]; // /dashboard/[orgId]/[projectId]/[page]

				let targetPath: string;
				if (currentPage && pathParts.length > 4) {
					// Preserve the current page when changing projects
					targetPath = `/dashboard/${project.organizationId}/${project.id}/${currentPage}`;
				} else {
					// Navigate to the new project dashboard
					targetPath = `/dashboard/${project.organizationId}/${project.id}`;
				}

				// Use transition for smoother UI updates
				startTransition(() => {
					router.push(targetPath as any);
					// Reset loading state after a short delay
					setTimeout(() => setIsNavigating(false), 500);
				});
			}
		},
		[router, pathname, startTransition],
	);

	return {
		selectedOrganization,
		selectedProject,
		organizations,
		projects,
		handleOrganizationSelect,
		handleProjectSelect,
		handleOrganizationCreated,
		handleProjectCreated,
		isNavigating: isNavigating || isPending,
		isLoadingData: isLoadingOrgs || isLoadingProjects,
	};
}
